import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Collapsible } from 'ncoded-component-library';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import {
  getArrayLength,
  getIsNodeFile,
  getIsNodeFolder,
  sortDataRoomNodes,
} from 'utils';
import FileInfoInstance from './components/FileInfoInstance';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import PlusIcon from 'icons/Plus.icon';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import { FileInfoInstanceProps } from './components/FileInfoInstance/FileInfoInstance.component';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import WarningBanner from 'components/WarningBanner';

import './FileInfo.styles.scss';

type FileInfoProps = {
  className?: string;
  isEmpty?: boolean;
  isLoading?: boolean;
  nodes?: DataRoomNode[];
  onFilesReselect: () => void;
} & Omit<FileInfoInstanceProps, 'node'>;

const FileInfo: React.FC<FileInfoProps> = (props) => {
  const {
    className,
    isLoading,
    isEmpty,
    nodes,
    checkedFileIds,
    onFilesReselect,
    ...rest
  } = props;

  const classes = classNames(
    'shareable-file-info',
    { 'shareable-file-info--empty': isEmpty },
    className,
  );

  const [collapse, collapseRef] = useCallbackRef<CollapsibleRef>();

  const { t } = useTranslation();

  const sortedNodes = useMemo(() => sortDataRoomNodes(nodes), [nodes]);

  const selectedLabel = useMemo(() => {
    if (nodes.every(getIsNodeFile)) return 'viewFiles';

    if (nodes.every(getIsNodeFolder)) return 'viewFolders';

    return 'viewItems';
  }, [nodes]);

  const foldersWithNewFiles = useMemo(
    () =>
      getArrayLength(
        nodes.filter(({ childrenIds }) =>
          childrenIds.some((id) => !checkedFileIds.includes(id)),
        ),
      ),
    [checkedFileIds, nodes],
  );

  const AddButton = useMemo(
    () => (
      <Button
        className={classNames({ 'new-feature': isEmpty })}
        variant="link"
        icon={PlusIcon}
        onClick={onFilesReselect}
      >
        {t('addMoreFiles')}
      </Button>
    ),
    [isEmpty, onFilesReselect, t],
  );

  if (isLoading) return null;

  if (isEmpty)
    return (
      <div className={classes}>
        <WarningBanner title={t('noFilesSelected')} />

        {AddButton}
      </div>
    );

  return (
    <Collapsible
      ref={collapseRef}
      className={classes}
      trigger={
        <p className={classNames('trigger', { open: collapse?.open })}>
          {t(selectedLabel, { count: nodes.length })} <ChevrondownIcon />
        </p>
      }
    >
      <>
        {!!foldersWithNewFiles && (
          <div className="new-files-banner">
            <InformationcircleIcon />
            <div>
              <p>{t('foldersHaveNewFiles', { count: foldersWithNewFiles })}</p>
              <p>{t('chooseToShareNewFiles')}</p>
            </div>
          </div>
        )}
        <div className="shareable-file-info__content">
          <ul>
            {sortedNodes.map((node) => (
              <FileInfoInstance
                key={node?._id}
                node={node}
                checkedFileIds={checkedFileIds}
                {...rest}
              />
            ))}
          </ul>
        </div>

        {AddButton}
      </>
    </Collapsible>
  );
};

export default FileInfo;
